/**
 * A labelled select, matching Field.jsx's accessibility contract.
 *
 * Same reasoning as Field: a real <label for>, a hint tied by aria-describedby,
 * and errors announced rather than merely coloured (Decision #18). The extra
 * piece here is `help` — a line of explanation under the chosen option, because
 * "Independently scheduled" means something specific to NCCCS funding and a
 * coordinator should not have to remember which formula it triggers.
 */
import { useId } from 'react';

export function Select({
  label,
  value,
  onChange,
  options,
  placeholder = 'Choose…',
  error,
  hint,
  help,
  required = true,
  optional, // see Field.jsx: required (validation) and optional (label) differ
  disabled = false,
}) {
  const id = useId();
  const showOptional = optional ?? !required;
  const hintId = hint ? `${id}-hint` : undefined;
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, helpId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {showOptional && <span className="muted"> (optional)</span>}
      </label>

      {hint && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}

      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={error ? 'true' : undefined}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Consequence of the current choice, not generic help text. */}
      {help && (
        <p className="field__help" id={helpId}>
          {help}
        </p>
      )}

      {error && (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default Select;
